import React, { useEffect, useState } from 'react';
import insuranceDetailsService from '../insuranceDetails/insuranceDetailsService';
import imageService from '../images/imageService';
import './insurancePage.css';

const insuranceService = new insuranceDetailsService();

const InsurancePage = () => {
  const [insuranceList, setInsuranceList] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const itemsPerPage = 9;
  const userRole = localStorage.getItem('role');
  const isAdmin = userRole === 'ADMIN';

  const getInsuranceId = (item) => {
    return item?.insid ?? item?.insId ?? item?.ins_id ?? item?.id;
  };

  const getInsuranceImageUrl = (item) => {
    if (item?.insPicture) {
      return imageService.getImageUrl('insurance', item.insPicture);
    }

    return '/images/insurance/default.jpg';
  };

  const buildImageFileName = (file, insuranceName, insuranceId) => {
    const extension = file.name.split('.').pop();
    const cleanName = (insuranceName || 'insurance')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    return `${cleanName}-${insuranceId}-${Date.now()}.${extension}`;
  };

  const loadInsurance = async () => {
    try {
      const data = await insuranceService.getAllInsuranceDetails();

      const filteredData = (data || []).filter((item) => {
        const id = getInsuranceId(item);
        return id !== 32 && id !== '32';
      });

      const withImageUrls = filteredData.map((item) => ({
        ...item,
        imageUrl: getInsuranceImageUrl(item),
      }));

      setInsuranceList(withImageUrls);
    } catch (err) {
      console.error('Error loading insurance:', err);
      setInsuranceList(null);
    }
  };

  useEffect(() => {
    loadInsurance();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredList = (insuranceList || []).filter((item) =>
    (item.insName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);

  const handlePageChange = (direction) => {
    if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const openDetails = (item) => {
    const id = getInsuranceId(item);

    const enrichedItem = {
      ...item,
      insid: id,
      insId: id,
      ins_id: id,
      imageUrl: item.imageUrl || getInsuranceImageUrl(item),
    };

    setSelectedItem(enrichedItem);
    setEditData(enrichedItem);
    setSelectedImageFile(null);
    setImagePreviewUrl('');
    setIsEditing(false);
  };

  const closePopup = () => {
    setSelectedItem(null);
    setEditData(null);
    setSelectedImageFile(null);
    setImagePreviewUrl('');
    setIsEditing(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setSelectedImageFile(null);
      setImagePreviewUrl('');
      return;
    }

    setSelectedImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    const id = getInsuranceId(editData);

    if (!id) {
      console.error('editData missing insurance ID:', editData);
      alert('Missing insurance ID. Cannot save.');
      return;
    }

    setSaving(true);

    try {
      let updatedPayload = {
        ...editData,
        insid: id,
        insId: id,
      };

      if (selectedImageFile) {
        const filename = buildImageFileName(
          selectedImageFile,
          updatedPayload.insName,
          id
        );

        await imageService.uploadImage(selectedImageFile, filename, 'insurance');

        updatedPayload = {
          ...updatedPayload,
          insPicture: filename,
        };
      }

      const saved = await insuranceService.updateInsuranceDetail(id, updatedPayload);

      const updatedItem = {
        ...updatedPayload,
        ...saved,
        insid: getInsuranceId(saved) || id,
        insId: getInsuranceId(saved) || id,
        ins_id: getInsuranceId(saved) || id,
      };

      updatedItem.imageUrl = getInsuranceImageUrl(updatedItem);

      const updatedList = insuranceList.map((item) => {
        const itemId = getInsuranceId(item);
        return String(itemId) === String(id) ? updatedItem : item;
      });

      setInsuranceList(updatedList);
      setSelectedItem(updatedItem);
      setEditData(updatedItem);
      setSelectedImageFile(null);
      setImagePreviewUrl('');
      setIsEditing(false);

      alert('Insurance updated successfully!');
    } catch (err) {
      console.error('Failed to update insurance:', err);
      alert('Failed to update insurance.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const id = getInsuranceId(selectedItem);

    if (!id) {
      alert('Missing insurance ID. Cannot delete.');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedItem.insName || 'this insurance'}?`
    );

    if (!confirmed) return;

    setDeleting(true);

    try {
      await insuranceService.deleteInsuranceDetail(id);

      setInsuranceList((prevList) =>
        (prevList || []).filter((item) => String(getInsuranceId(item)) !== String(id))
      );

      closePopup();
      alert('Insurance deleted successfully!');
    } catch (err) {
      console.error('Failed to delete insurance:', err);
      alert('Failed to delete insurance.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div id="insurance-page-wrapper">
      <div className="insurance-description">
        <h1>Insurance Coverage Directory</h1>
        <p>
          Here you can browse the insurance providers we currently work with. Each entry includes
          details such as authorization requirements, office address, coverage days, and acceptance
          status. This page is designed to help you quickly understand which insurances are accepted
          and the conditions that may apply.
        </p>
        <p>
          Please note: if your insurance provider is not listed here, that does not necessarily mean
          it isn’t accepted. Coverage may vary depending on your specific plan. We encourage you to
          <strong> call or email our office</strong> for confirmation. Our team regularly updates
          this directory to reflect new insurances as they are added.
        </p>
      </div>

      <p className="info-note">
        <strong>Note:</strong> If acceptance is marked as <em>'patient'</em>, it means coverage
        depends on the individual patient's insurance.
      </p>

      <input
        type="text"
        placeholder="Search insurance by name"
        className="insurance-search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {insuranceList === null ? (
        <p>Failed to load insurance details.</p>
      ) : (
        <div className="insurance-grid">
          {currentItems.map((item) => (
            <div
              key={getInsuranceId(item)}
              className="insurance-card"
              onClick={() => openDetails(item)}
            >
              <img
                src={item.imageUrl}
                alt={item.insName}
                className="insurance-thumbnail"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/images/insurance/default.jpg';
                }}
              />

              <div className="insurance-text">
                <strong>{item.insName}</strong>
                <div>{item.address}</div>
              </div>

              <div className="view-more">More Details</div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => handlePageChange('prev')} disabled={currentPage === 1}>
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button onClick={() => handlePageChange('next')} disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      )}

      {selectedItem && (
        <>
          <div className="overlay" onClick={closePopup} />

          <div className="popup animate-popup">
            <span className="popup-close" onClick={closePopup}>
              &times;
            </span>

            <img
              src={imagePreviewUrl || selectedItem.imageUrl}
              alt={selectedItem.insName}
              className="popup-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/images/insurance/default.jpg';
              }}
            />

            {isEditing ? (
              <>
                <input
                  type="text"
                  value={editData.insName || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      insName: e.target.value,
                    })
                  }
                  placeholder="Name"
                />

                <input
                  type="text"
                  value={editData.authorization || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      authorization: e.target.value,
                    })
                  }
                  placeholder="Authorization"
                />

                <input
                  type="text"
                  value={editData.address || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      address: e.target.value,
                    })
                  }
                  placeholder="Address"
                />

                <input
                  type="number"
                  value={editData.days ?? ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      days: Number(e.target.value),
                    })
                  }
                  placeholder="Days"
                />

                <input
                  type="text"
                  value={editData.acceptance || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      acceptance: e.target.value,
                    })
                  }
                  placeholder="Acceptance"
                />

                <input
                  type="text"
                  value={editData.insCode || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      insCode: e.target.value,
                    })
                  }
                  placeholder="Insurance Code"
                />

                <input
                  type="text"
                  value={editData.coverageName || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      coverageName: e.target.value,
                    })
                  }
                  placeholder="Coverage Name"
                />

                <label className="insurance-upload-label">
                  Insurance Picture
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>

                {selectedImageFile && (
                  <p className="selected-image-name">
                    Selected image: {selectedImageFile.name}
                  </p>
                )}

                <button
                  className="save-button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <h3>{selectedItem.insName}</h3>

                <p>
                  <strong>Authorization:</strong> {selectedItem.authorization || 'N/A'}
                </p>

                <p>
                  <strong>Address:</strong> {selectedItem.address || 'N/A'}
                </p>

                <p>
                  <strong>Days:</strong> {selectedItem.days ?? 'N/A'}
                </p>

                <p>
                  <strong>Insurance Code:</strong> {selectedItem.insCode || 'N/A'}
                </p>

                <p>
                  <strong>Coverage Name:</strong> {selectedItem.coverageName || 'N/A'}
                </p>

                <p>
                  <strong>Acceptance:</strong>{' '}
                  <span
                    className={
                      selectedItem.acceptance?.toLowerCase() === 'yes'
                        ? 'accept-green'
                        : selectedItem.acceptance?.toLowerCase() === 'no'
                        ? 'accept-red'
                        : 'accept-yellow'
                    }
                  >
                    {selectedItem.acceptance || 'N/A'}
                  </span>
                </p>
              </>
            )}

            {isAdmin && (
              <div className="admin-popup-actions">
                <button
                  className="edit-button"
                  onClick={() => {
                    setIsEditing((prev) => !prev);
                    setSelectedImageFile(null);
                    setImagePreviewUrl('');
                    setEditData(selectedItem);
                  }}
                  disabled={saving || deleting}
                >
                  {isEditing ? 'Cancel' : 'Edit'}
                </button>

                <button
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving || deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InsurancePage;